import {
    Box,
    Dialog,
    Skeleton,
    styled,
    Typography,
    useTheme,
} from '@mui/material'
import { Button } from 'components'
import TreeNode from 'components/Filters/EidFilter/GenericFilter/TreeViewWithSearch/components/TreeWithSearch/TreeNode'
import { InputBox } from 'components/Popups/EditAssignments'
import { useTreeFilterAPI, useUpdateDirectAssignedLocations } from 'hooks'
import { useDebounce } from 'packages/core'
import { Icon } from 'packages/eid-icons'
import { Tooltip, useNotification } from 'packages/eid-ui'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { TREE_VIEW_LEVELS } from 'utils'

const CustomDialog = styled(Dialog)({
    '& .MuiDialog-paperWidthSm': {
        maxWidth: '56.0rem',
        width: '100%',
        background: '#f7f8fa',
        '& .MuiBox-root': {
            maxWidth: '83.0rem',
        },
    },
    '& .MuiBox-root': {
        border: 'none',
        background: ' #f7f8fa',
        position: 'relative',
    },
    '& .MuiDialog-paper': {
        maxWidth: '44rem',
    },
})

const SearchInput = styled(Box)({
    ' & input': {
        height: '4.0rem',
        marginTop: '1.2rem',
        fontSize: '1.6rem',
        '&::placeholder': {
            color: '#b4b4b4',
        },
    },
})
const CloseIconeBox = styled(Box)({
    '& svg': {
        position: 'absolute',
        top: '-2.0rem',
        right: '-2.0rem',
        cursor: 'pointer',
    },
})

const RoleBoxCardInner = styled(Box)({
    maxHeight: '20.0rem',
    overflowY: 'auto',
    width: '100%',
    margin: '2.4rem 0',
})

const NewAccessAssignmentModal = styled(Box)({
    '& h2': {
        fontSize: '2.4rem',
        color: '#1b1f1f',
        fontWeight: 'normal',
    },
})
const AssignmentModalHeader = styled(Box)({
    padding: '3.2rem 4.0rem',
    '& svg': {
        top: '-1.4rem !important',
    },
})

const NewDirectAssigned = styled(Box)({
    padding: ' 0 4.0rem',
    borderBottom: '0.1rem solid #d8d8d8 !important',
})

const AssignmentModalFooter = styled(Box)(({ theme }) => ({
    display: 'flex',
    padding: '3.2rem 4.0rem',
    gap: '1.6rem',

    '& button': {
        border: `solid 0.1rem ${theme?.palette?.primary?.main}`,
        padding: '0.8rem 1.2rem',
        height: '3.2rem',
        fontSize: '1.4rem',
        '&:nth-child(1)': {
            background: theme?.palette?.primary?.main,
            color: '#fff',
        },
        '&:nth-child(2)': {
            color: theme?.palette?.primary?.main,
            maxWidth: '9.0rem',
        },
    },
}))

const MIN_CHARACTERS_REQUIRED_TO_SEARCH = 3

const AddDirectAssignedLocation = ({ resourceId, refetch, list }) => {
    const { t } = useTranslation()
    const theme = useTheme()

    const [searchedLocation, setSearchedLocation] = useState('')
    const debouncedSearchLocationValue = useDebounce(searchedLocation)
    const [selectedLocation, setSelectedLocation] = useState(null)
    const [isAlreadyAssigned, setIsAlreadyAssigned] = useState(false)
    const { showWarningMessage } = useNotification()
    const [openModal, setOpenModal] = useState(false)
    const { data: locationTree, isLoading } = useTreeFilterAPI(
        `/api/Lookups/getBusinessLocationTree?locationFor=1&levelsToLoad=${TREE_VIEW_LEVELS}`,
        undefined,
        undefined,
        debouncedSearchLocationValue?.length >=
            MIN_CHARACTERS_REQUIRED_TO_SEARCH
            ? debouncedSearchLocationValue
            : '',
        openModal,
    )

    const [updateDirectAssignedLocations, { isLoading: updatingLocation }] =
        useUpdateDirectAssignedLocations()

    useEffect(() => {
        if (selectedLocation) {
            const isSelectedExist = list.find((x) => x.id === selectedLocation)
            if (isSelectedExist) {
                setIsAlreadyAssigned(true)
            } else {
                setIsAlreadyAssigned(false)
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [list?.length])

    const handleSelect = (id) => {
        try {
            const isSelectedExist = list.find((x) => x.id === id)
            if (isSelectedExist) {
                setIsAlreadyAssigned(true)
            } else {
                setIsAlreadyAssigned(false)
            }
            setSelectedLocation(id)
        } catch (err) {}
    }
    const handleSave = () => {
        if (!isAlreadyAssigned) {
            const dataObject = {
                locationsToAdd: [selectedLocation],
                resourceId: resourceId,
            }
            updateDirectAssignedLocations(dataObject).then((data) => {
                refetch()
                setOpenModal(false)
            })
        } else {
            showWarningMessage(t('DirectLocationAlreadyAssigned'))
        }
    }
    return (
        <>
            <Button
                onClick={() => setOpenModal(true)}
                style={{
                    borderRadius: '0.4rem',
                    padding: '1.2rem',
                    height: '4rem',
                    width: 'fit-content',
                    backgroundColor: theme?.palette?.primary?.main,
                    color: '#ffffff',
                    fontSize: '1.4rem',
                    '&:hover': {
                        backgroundColor: theme?.palette?.primary?.main,
                    },
                }}
            >
                {t('AddNewDirectAssignedLocation')}
            </Button>
            <CustomDialog
                open={openModal}
                onClose={() => setOpenModal(false)}
                title={t('')}
            >
                <NewAccessAssignmentModal>
                    <AssignmentModalHeader>
                        <CloseIconeBox onClick={() => setOpenModal(false)}>
                            <Icon
                                name="Close"
                                width="1.6rem"
                                height="1.6rem"
                                color="#959598"
                            />
                        </CloseIconeBox>
                        <Typography variant="h2">
                            {t('AddNewDirectAssignedLocation')}
                        </Typography>
                    </AssignmentModalHeader>
                    <NewDirectAssigned>
                        <SearchInput>
                            <InputBox>
                                <Typography variant="subtitle1">
                                    {t('SelectLocation')}
                                </Typography>
                                <input
                                    type="text"
                                    placeholder={t('SearchLocation')}
                                    value={searchedLocation}
                                    onChange={(e) =>
                                        setSearchedLocation(e.target.value)
                                    }
                                ></input>
                            </InputBox>
                        </SearchInput>
                        <Box>
                            <RoleBoxCardInner>
                                {isLoading ? (
                                    <Box>
                                        <Skeleton height={20} />
                                        <Skeleton height={20} />
                                    </Box>
                                ) : (
                                    <TreeNode
                                        handleSelect={(val) => {
                                            handleSelect(val?.id)
                                        }}
                                        explorer={locationTree || {}}
                                        value={selectedLocation}
                                        highlightText={searchedLocation}
                                        titleProp={'friendlyName'}
                                        valueProp={'id'}
                                        url={`/api/Lookups/getBusinessLocationTree?locationFor=1&levelsToLoad=${TREE_VIEW_LEVELS}`}
                                        showCollapseIcon={false}
                                        showRadioToSelect={false}
                                        disableAssignableCheck={true}
                                    />
                                )}
                            </RoleBoxCardInner>
                        </Box>
                    </NewDirectAssigned>
                    <AssignmentModalFooter>
                        <Tooltip
                            title={
                                isAlreadyAssigned
                                    ? t('DirectLocationAlreadyAssigned')
                                    : ''
                            }
                        >
                            <Box>
                                <Button
                                    backgroundColor={
                                        theme?.palette?.primary?.main
                                    }
                                    onClick={() => handleSave()}
                                    loading={updatingLocation}
                                    disabled={
                                        !selectedLocation || isAlreadyAssigned
                                    }
                                    disabledStyles={{
                                        backgroundColor: '#aab0b4 !important',
                                        borderColor: '#aab0b4 !important',
                                        color: '#ffffff !important',
                                    }}
                                >
                                    {t('AddNewDirectAssignedLocation')}
                                </Button>
                            </Box>
                        </Tooltip>
                        <Button onClick={() => setOpenModal(false)}>
                            {t('Cancel')}
                        </Button>
                    </AssignmentModalFooter>
                </NewAccessAssignmentModal>
            </CustomDialog>
        </>
    )
}

export default AddDirectAssignedLocation
